/**
 * BROADCAST CLIENT - Cliente SSE unificado para front-tv y front-customer
 *
 * Features:
 * - MessageStabilizer integrado para filtrar duplicados/desordenados
 * - Watchdog de 3s para detectar pérdida de conexión
 * - Exponential backoff para reconexiones
 * - Handlers persistentes que sobreviven reconnect
 *
 * Uso (front-tv):
 *   BroadcastClient.connect('http://...', 'TV-001', '1');
 *
 * Uso (front-customer):
 *   BroadcastClient.connect({ serverUrl, clientId, channel, privateChannel });
 *
 * Eventos:
 *   BroadcastClient.on('sorteo:inicio', (data) => { ... });
 */

// ============================================================
// MESSAGE STABILIZER (inline)
// Filtra duplicados usando msgId del servidor (0-999)
// ============================================================
const seen = new Set();

// Limpiar cada 30s para no llenar memoria (msgId es 0-999)
if (typeof setInterval !== 'undefined') {
    setInterval(() => seen.clear(), 30000);
}

function stabilizerShouldProcess(msg) {
    // Solo filtrar si tiene msgId (mensajes críticos de Go)
    // Key incluye sorteoConfigId para multi-canal (cada sorteo tiene su propia secuencia de msgId)
    if (msg.msgId !== undefined) {
        const key = msg.sorteoConfigId != null ? msg.sorteoConfigId + ':' + msg.msgId : msg.msgId;
        if (seen.has(key)) return false;
        seen.add(key);
    }
    return true;
}

function stabilizerReset() {
    // NO limpiar en reconexión - el msgId es secuencial del servidor
}

export const MessageStabilizer = {
    shouldProcess: stabilizerShouldProcess,
    reset: stabilizerReset,
};

// ============================================================
// CONFIGURACIÓN
// ============================================================
const CONFIG = {
    reconnectDelayBase: 1000,
    reconnectDelayMax: 30000,
    // Reintentos eternos: una TV de display vive 24/7, el backend puede caer
    // minutos u horas — el cliente debe seguir intentando para siempre. El
    // exponential backoff con cap a 30s da ~120 intentos/hora sin hostigar.
    // Cuando se cierra la pestaña, el navegador limpia el EventSource solo.
    inestabilidadTimeout: 5000, // Warning: 5s sin mensajes (antes 2s, demasiado agresivo)
    watchdogTimeout: 10000, // Reconectar: 10s sin mensajes (antes 4s, causaba reconexiones falsas)
    debug: true,
};

// ============================================================
// ESTADO
// ============================================================
let eventSource = null;
let handlers = {};
let persistentHandlers = {};
let serverUrl = '';
let clientId = '';
let channel = '';
let privateChannel = null;
let sorteoActivo = '';
let nodoActivo = '';
let reconnectAttempts = 0;
let connected = false;
let reconnectTimeout = null;
// SUPERSEDED: otra instancia con el MISMO serial tomó la conexión (el
// broadcaster hace kick-old: 1 sesión por serial). Sin este estado, el
// cliente pateado ve un error genérico y reintenta cada 2s → dos instancias
// se patean mutuamente para siempre (caso real TV-MP3O9UVY: 2.656 kicks +
// rate-limit 429, 2026-07-10). Con él: reintento LENTO (~90-120s) — si la
// otra instancia desapareció, esta se recupera sola; si sigue viva, la
// "guerra" baja a un kick por minuto y medio con aviso en pantalla (el
// front escucha .on('superseded') para el overlay "abierta en otro lugar").
let supersededPorOtraInstancia = false;
const SUPERSEDED_RETRY_BASE_MS = 90000;
const SUPERSEDED_RETRY_JITTER_MS = 30000; // evita que 2 instancias reintenten sincronizadas
let watchdogTimer = null;
let inestabilidadTimer = null;
let inestabilidadEmitida = false;
let logFilter = null;

// ============================================================
// LOGGING
// ============================================================
function log(...args) {
    if (CONFIG.debug) console.log('[BroadcastClient]', ...args);
}

function logError(...args) {
    console.error('[BroadcastClient]', ...args);
}

// ============================================================
// WATCHDOG + INESTABILIDAD
// ============================================================
function resetWatchdog() {
    // Limpiar timers anteriores
    clearTimeout(inestabilidadTimer);
    clearTimeout(watchdogTimer);

    // Si habiamos emitido inestabilidad, ahora estamos estables de nuevo
    if (inestabilidadEmitida) {
        inestabilidadEmitida = false;
        emit('estabilidad_recuperada', {});
    }

    // Timer de inestabilidad (warning temprano)
    inestabilidadTimer = setTimeout(() => {
        if (!inestabilidadEmitida) {
            inestabilidadEmitida = true;
            log(`Inestabilidad: Sin mensajes en ${CONFIG.inestabilidadTimeout / 1000}s, conexion inestable...`);
            emit('inestabilidad_detectada', {});
        }
    }, CONFIG.inestabilidadTimeout);

    // Timer de watchdog (reconexion)
    watchdogTimer = setTimeout(() => {
        log(`Watchdog: Sin mensajes en ${CONFIG.watchdogTimeout / 1000}s, reconectando...`);
        emit('watchdog_triggered', {});
        reconnect();
    }, CONFIG.watchdogTimeout);
}

function stopWatchdog() {
    clearTimeout(inestabilidadTimer);
    clearTimeout(watchdogTimer);
    inestabilidadTimer = null;
    watchdogTimer = null;
    inestabilidadEmitida = false;
}

// ============================================================
// EXPONENTIAL BACKOFF
// ============================================================
function getReconnectDelay() {
    return Math.min(CONFIG.reconnectDelayBase * Math.pow(2, reconnectAttempts), CONFIG.reconnectDelayMax);
}

// ============================================================
// CONEXIÓN SSE
// ============================================================

/**
 * Conectar al broadcast server
 *
 * Forma 1 (front-tv): connect(url, serial, sorteoId)
 * Forma 2 (front-customer): connect({ serverUrl, clientId, channel, privateChannel })
 */
export function connect(urlOrOptions, serial, sorteoId) {
    // Detectar forma de llamada
    if (typeof urlOrOptions === 'object') {
        // Forma 2: objeto de opciones
        serverUrl = urlOrOptions.serverUrl;
        clientId = urlOrOptions.clientId;
        privateChannel = urlOrOptions.privateChannel || null;
        channel = privateChannel ? `${urlOrOptions.channel},user:${privateChannel}` : urlOrOptions.channel;
    } else {
        // Forma 1: parámetros individuales
        serverUrl = urlOrOptions;
        clientId = serial;
        channel = sorteoId;
        privateChannel = null;
    }

    // Limpiar timeout de reconexión pendiente
    if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
    }

    if (eventSource) {
        eventSource.close();
    }

    // Matar los timers del intento anterior ANTES de abrir el EventSource nuevo.
    // Sin esto, el inestabilidadTimer/watchdogTimer del intento previo seguían
    // vivos y disparaban sobre la conexión nueva → "inestable/reconectar" a los
    // ~0.3s en vez de los 5s/10s configurados, amplificando el loop de
    // reconexión en hardware con SSE frágil (WebView viejo de Bromite). El
    // onopen vuelve a armar timers limpios con resetWatchdog().
    stopWatchdog();

    // deviceId opcional: cuando esta presente y el cliente solicita un canal
    // privado user:*, el broadcaster lo valida contra el backend (device
    // anchoring). Sin deviceId, los user:* son rechazados — el resto
    // (sorteo, device, cashier, tick) sigue funcionando sin cambios.
    const deviceIdParam =
        typeof urlOrOptions === 'object' && urlOrOptions.deviceId
            ? `&deviceId=${encodeURIComponent(urlOrOptions.deviceId)}`
            : '';
    // sorteoActivo: opcional. Sorteo que el cliente VE (no los que escucha para
    // historial) — alimenta el conteo de jugadores por sorteo en /stats. Solo se
    // adjunta en la forma-objeto; el Go lo ignora si falta. Se persiste en la
    // variable de módulo para que las reconexiones lo reenvíen.
    if (typeof urlOrOptions === 'object' && urlOrOptions.sorteoActivo != null) {
        sorteoActivo = String(urlOrOptions.sorteoActivo);
    }
    if (typeof urlOrOptions === 'object' && urlOrOptions.nodoActivo != null) {
        nodoActivo = String(urlOrOptions.nodoActivo);
    }
    const sorteoActivoParam = sorteoActivo ? `&sorteoActivo=${encodeURIComponent(sorteoActivo)}` : '';
    const nodoActivoParam = nodoActivo ? `&nodoActivo=${encodeURIComponent(nodoActivo)}` : '';
    const sseUrl = `${serverUrl}/events?serial=${encodeURIComponent(clientId)}&channel=${encodeURIComponent(channel)}${deviceIdParam}${sorteoActivoParam}${nodoActivoParam}`;
    log(`Conectando a: ${sseUrl}`);
    emit('connecting', { clientId, channel });

    eventSource = new EventSource(sseUrl);

    eventSource.onopen = () => {
        connected = true;
        reconnectAttempts = 0;
        supersededPorOtraInstancia = false; // recuperado: la otra instancia ya no está (o nos toca a nosotros)
        log('Conectado al broadcast server');
        MessageStabilizer.reset();
        resetWatchdog();
        emit('connected', { clientId, channel });
    };

    eventSource.onmessage = event => {
        try {
            const message = JSON.parse(event.data);
            const { channel, event: eventName, data } = message;

            // SUPERSEDED: el broadcaster nos avisa que OTRA instancia con el
            // mismo serial tomó la conexión (y va a cerrarnos ya mismo). NO
            // pelear: avisar al front (overlay), cerrar limpio, y reintentar
            // LENTO — si la otra instancia se fue, nos recuperamos solos.
            if (eventName === 'superseded') {
                supersededPorOtraInstancia = true;
                connected = false;
                logError('Superseded: otra instancia con este serial tomó la conexión', data);
                emit('superseded', data || {});
                stopWatchdog();
                if (eventSource) {
                    eventSource.close();
                    eventSource = null;
                }
                const delay = SUPERSEDED_RETRY_BASE_MS + Math.floor(Math.random() * SUPERSEDED_RETRY_JITTER_MS);
                log(`Reintento lento post-superseded en ${Math.round(delay / 1000)}s`);
                if (reconnectTimeout) clearTimeout(reconnectTimeout);
                reconnectTimeout = setTimeout(() => {
                    supersededPorOtraInstancia = false;
                    reconnect();
                }, delay);
                return;
            }

            // Inyectar canal de origen para que los handlers distingan privado vs público
            if (channel !== undefined) data._channel = channel;

            resetWatchdog();

            // Filtrar con MessageStabilizer (silencioso — son duplicados esperados)
            const msgForFilter = { ...data, event: eventName };
            if (!MessageStabilizer.shouldProcess(msgForFilter)) return;

            // Log opcional. Cliente puede inyectar `logFilter(event, data)`
            // para silenciar lo que no le importa (ej. customer ignora sorteos
            // distintos al activo). Sin filtro inyectado: log universal salvo
            // tick (lo confirma el watchdog).
            if (eventName !== 'tick') {
                const incluye = typeof logFilter === 'function' ? logFilter(eventName, data) : true;
                if (incluye) log(`Evento recibido: ${eventName}`, data);
            }

            if (persistentHandlers[eventName]) {
                persistentHandlers[eventName](data);
            }
            if (handlers[eventName]) {
                handlers[eventName](data);
            }
            if (handlers['*']) {
                handlers['*'](eventName, data);
            }
        } catch (error) {
            logError('Error parseando mensaje:', error, event.data);
        }
    };

    eventSource.onerror = error => {
        // Post-superseded el server nos cierra: ese error es esperado y el
        // reintento LENTO ya quedó agendado — no pisarlo con el backoff normal
        // de 2s (recrearía la guerra de kicks que superseded vino a matar).
        if (supersededPorOtraInstancia) {
            log('Error SSE ignorado (superseded — reintento lento ya agendado)');
            return;
        }
        const wasConnected = connected;
        connected = false;
        logError('Error en conexión SSE:', error);

        if (wasConnected) {
            emit('disconnected', { error });
        }

        reconnectAttempts++;
        const delay = getReconnectDelay();
        log(`Reconectando en ${delay}ms (intento ${reconnectAttempts}, eterno)`);

        reconnectTimeout = setTimeout(() => {
            // Reconectar con la misma forma
            if (privateChannel !== null) {
                connect({ serverUrl, clientId, channel: channel.split(',')[0], privateChannel });
            } else {
                connect(serverUrl, clientId, channel);
            }
        }, delay);
    };
}

export function disconnect(clearAllHandlers = false) {
    if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
    }
    stopWatchdog();

    if (eventSource) {
        eventSource.close();
        eventSource = null;
        connected = false;
        log('Desconectado');
        emit('disconnected', { manual: true });
    }

    if (clearAllHandlers) {
        handlers = {};
        persistentHandlers = {};
    }
}

export function reconnect() {
    log('Reconexión solicitada');
    // NO resetear reconnectAttempts acá — eso rompe el backoff exponencial real
    // cuando el server está caído. Solo se resetea en onopen (handshake OK
    // confirmado). Antes este reset hacía que cada reconnect arrancara de 1s,
    // martillando al backend caído sin crecer a 30s como dice CONFIG.
    MessageStabilizer.reset();
    if (clientId && serverUrl) {
        if (privateChannel !== null) {
            connect({ serverUrl, clientId, channel: channel.split(',')[0], privateChannel });
        } else {
            connect(serverUrl, clientId, channel);
        }
    } else {
        logError('No hay clientId/serverUrl para reconectar');
    }
}

// ============================================================
// LISTENERS DEL BROWSER PARA REVIVIR LA CONEXIÓN
// El backoff exponencial puede llegar a 30s entre intentos. Cuando el SO o
// el browser nos avisan que la red volvió o la pestaña recuperó foco, no
// esperamos al próximo tick del backoff: forzamos reconexión inmediata.
// Esto reduce el tiempo de detección de "backend volvió" de hasta 30s a
// segundos, sin hostigar al backend cuando está caído (los listeners no
// disparan en bucle, solo en transiciones reales del SO/browser).
// ============================================================
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('online', () => {
        if (clientId && serverUrl && !connected) {
            log('Evento "online" del navegador: forzando reconexión inmediata');
            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
                reconnectTimeout = null;
            }
            reconnect();
        }
    });

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && clientId && serverUrl && !connected) {
            log('Pestaña visible: forzando reconexión inmediata');
            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
                reconnectTimeout = null;
            }
            reconnect();
        }
        // Pausar watchdog cuando la pestaña está oculta — los browsers pausan
        // setTimeout en background tabs (Chrome ≥ 88 throttle a 1/min), por lo
        // que el watchdog de 10s sin mensajes "explota" tarde y dispara
        // reconnect aunque el SSE estuviera sano. Al volver visible, el
        // listener de arriba ya reconecta si hace falta; si seguía conectado,
        // resetWatchdog vuelve a armar el timer normal con el próximo tick.
        // Nota: NO toca connected — si EventSource cerró durante el background
        // (server caído real), onerror ya disparó y reconnect está agendado.
        if (document.visibilityState === 'hidden') {
            stopWatchdog();
        }
    });
}

// ============================================================
// EVENT HANDLERS
// ============================================================

export function on(eventName, handler, persistent = false) {
    if (persistent) {
        persistentHandlers[eventName] = handler;
    } else {
        handlers[eventName] = handler;
    }
}

export function off(eventName) {
    delete handlers[eventName];
    delete persistentHandlers[eventName];
}

export function clearHandlers() {
    handlers = {};
}

function emit(eventName, data) {
    if (persistentHandlers[eventName]) {
        persistentHandlers[eventName](data);
    }
    if (handlers[eventName]) {
        handlers[eventName](data);
    }
}

/**
 * Inyecta un evento como si hubiera llegado del servidor: despacha a los mismos
 * handlers (persistente + normal + comodín `*`) que `onmessage`, en el mismo
 * orden. Para herramientas de testing que simulan eventos SSE entrantes — el
 * evento recorre el camino REAL del cliente, no un atajo. NO toca la conexión.
 */
export function simularEventoEntrante(eventName, data) {
    if (persistentHandlers[eventName]) persistentHandlers[eventName](data);
    if (handlers[eventName]) handlers[eventName](data);
    if (handlers['*']) handlers['*'](eventName, data);
}

// ============================================================
// GETTERS / SETTERS
// ============================================================

export function isConnected() {
    return connected;
}
export function getSerial() {
    return clientId;
}
export function getClientId() {
    return clientId;
}
export function getChannel() {
    return channel;
}
export function setDebug(enabled) {
    CONFIG.debug = enabled;
}
export function setWatchdogTimeout(ms) {
    CONFIG.watchdogTimeout = ms;
}

// El cliente inyecta una funcion (eventName, data) -> boolean para decidir
// que eventos logear. Usado por customer para silenciar logs de sorteos
// que no son el activo (el SSE igual los procesa para alimentar el lobby).
export function setLogFilter(fn) {
    logFilter = typeof fn === 'function' ? fn : null;
}

// Actualiza el sorteo activo (el que el cliente VE) sin reconectar. La nueva
// reconexión (watchdog/red) lo reenviará al Go. No fuerza reconexión: el cambio
// de sorteo en caliente NO debe reconectar (chocaría con el rate-limit por serial).
export function setSorteoActivo(id) {
    sorteoActivo = id != null ? String(id) : '';
}

// ============================================================
// NAMESPACE EXPORT
// ============================================================

export const BroadcastClient = {
    connect,
    disconnect,
    reconnect,
    on,
    off,
    clearHandlers,
    isConnected,
    getSerial,
    getClientId,
    getChannel,
    setDebug,
    setWatchdogTimeout,
    setLogFilter,
    setSorteoActivo,
    emit,
    simularEventoEntrante,
    MessageStabilizer,
};

export default BroadcastClient;
